/**
 * DriverEarningsPage · v2.6.9 batch BL
 * =====================================
 * Driver-side breakdown of earnings across Today / Week / All-Time.
 * Pulls /api/driver/earnings, renders a full data table plus 3 KPI cards.
 *
 * Tip Status is derived from the maintenance_fee_status snapshot the driver
 * meter writes to each trip — interpretation:
 *   · cash_collected > 0  → "Cash Collected"
 *   · card_collected > 0  → "Pending Payout" (Square settles on its schedule)
 */
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import DeliveryEarningsSection from "@/components/driver/DeliveryEarningsSection";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, DollarSign, Route, Star, Banknote, CreditCard, ReceiptText, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";

const BUCKETS = [
  { key: "today", label: "Today", testid: "earnings-bucket-today" },
  { key: "week", label: "This Week", testid: "earnings-bucket-week" },
  { key: "all_time", label: "All Time", testid: "earnings-bucket-all-time" },
];

function StatPill({ label, value, Icon, accent = "amber", testid }) {
  const ring = {
    amber: "border-amber-400/40 text-amber-300",
    emerald: "border-emerald-400/40 text-emerald-300",
    indigo: "border-indigo-400/40 text-indigo-300",
    zinc: "border-zinc-700 text-zinc-200",
  }[accent];
  return (
    <div className={`rounded-lg border ${ring} bg-[#121212] px-3 py-2.5 flex items-center gap-2`} data-testid={testid}>
      <Icon className="h-4 w-4 flex-shrink-0"/>
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 truncate">{label}</div>
        <div className="meter-display text-sm font-extrabold truncate">{value}</div>
      </div>
    </div>
  );
}

export default function DriverEarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/driver/earnings")
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load earnings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  // Poll every 30s to keep "Today" live while the driver is parked on the page.
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <Layout title="Earnings">
      <div className="space-y-5" data-testid="driver-earnings-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold">Earnings</h1>
            <p className="text-xs text-zinc-400">Live rollup of your fares, miles, tips, and payout status.</p>
          </div>
          <Button data-testid="earnings-refresh" onClick={load} variant="outline" className="bg-zinc-950 border-zinc-700 text-zinc-200">
            {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : "Refresh"}
          </Button>
        </div>

        {error && (
          <Card className="bg-red-500/10 border-red-500/40 text-red-200 p-4 text-sm" data-testid="earnings-error">{error}</Card>
        )}

        {loading && !data && (
          <Card className="bg-zinc-900 border-zinc-800 p-10 text-center text-zinc-500">
            <Loader2 className="h-6 w-6 animate-spin mx-auto"/>
            <div className="mt-2 text-xs uppercase tracking-widest">Loading earnings…</div>
          </Card>
        )}

        {data && (
          <>
            {/* KPI grid — three buckets stacked on mobile, columns on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="earnings-kpi-grid">
              {BUCKETS.map((b) => {
                const v = data[b.key] || {};
                return (
                  <Card key={b.key} data-testid={b.testid} className="bg-[#1A1A1A] border-zinc-800 p-5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{b.label}</div>
                    <div className="meter-display text-4xl font-extrabold text-amber-400 leading-none mt-2" data-testid={`${b.testid}-total`}>
                      {fmtUsd(v.total)}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">{v.count || 0} trip{v.count === 1 ? "" : "s"} · {Math.round(v.miles || 0)} mi · avg {fmtUsd(v.avg_per_trip)}</div>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <StatPill testid={`${b.testid}-cash`} label="Cash" value={fmtUsd(v.cash_collected)} Icon={Banknote} accent="emerald"/>
                      <StatPill testid={`${b.testid}-card`} label="Card" value={fmtUsd(v.card_collected)} Icon={CreditCard} accent="indigo"/>
                      <StatPill testid={`${b.testid}-tips`} label="Tips" value={fmtUsd(v.tips)} Icon={Star} accent="emerald"/>
                      <StatPill testid={`${b.testid}-tech`} label="Tech Fee" value={fmtUsd(v.tech_fee)} Icon={ReceiptText} accent="zinc"/>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* v2.7.58+E37 · DELIVERY SYSTEM Section 5 · surface the
                driver's multi-stop payout ledger totals right under the
                taxi-trip KPIs so the driver sees ALL income streams in
                one place. */}
            <DeliveryEarningsSection/>

            {/* Full breakdown table */}
            <Card className="bg-zinc-900 border-zinc-800 p-0 overflow-hidden" data-testid="earnings-table-card">
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-amber-400"/>
                <h2 className="text-sm font-bold uppercase tracking-widest">Breakdown</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="earnings-table">
                  <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-bold">Metric</th>
                      <th className="text-right px-4 py-2 font-bold">Today</th>
                      <th className="text-right px-4 py-2 font-bold">Week</th>
                      <th className="text-right px-4 py-2 font-bold">All Time</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-200">
                    {[
                      { label: "Gross Earnings", k: "total", fmt: fmtUsd, hi: true },
                      { label: "Trip Count", k: "count", fmt: (n) => n ?? 0 },
                      { label: "Miles Driven", k: "miles", fmt: (n) => `${(n || 0).toFixed(1)} mi` },
                      { label: "Avg per Trip", k: "avg_per_trip", fmt: fmtUsd },
                      { label: "Cash Collected", k: "cash_collected", fmt: fmtUsd },
                      { label: "Card Collected", k: "card_collected", fmt: fmtUsd },
                      { label: "Tips", k: "tips", fmt: fmtUsd },
                      { label: "Technology Fee", k: "tech_fee", fmt: fmtUsd },
                      { label: "Franchise Fee", k: "franchise_fee", fmt: fmtUsd },
                      { label: "Taxes Earned", k: "taxes", fmt: fmtUsd },
                    ].map((row) => (
                      <tr key={row.k} className="border-t border-zinc-800" data-testid={`earnings-row-${row.k}`}>
                        <td className="px-4 py-2.5 text-zinc-400 text-xs">{row.label}</td>
                        <td className={`px-4 py-2.5 text-right meter-display ${row.hi ? "text-amber-400 font-extrabold" : ""}`}>{row.fmt(data.today?.[row.k])}</td>
                        <td className={`px-4 py-2.5 text-right meter-display ${row.hi ? "text-amber-400 font-extrabold" : ""}`}>{row.fmt(data.week?.[row.k])}</td>
                        <td className={`px-4 py-2.5 text-right meter-display ${row.hi ? "text-amber-400 font-extrabold" : ""}`}>{row.fmt(data.all_time?.[row.k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-zinc-800 bg-[#0A0A0A] text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between flex-wrap gap-2" data-testid="earnings-payout-status">
                <span className="flex items-center gap-1.5"><Banknote className="h-3 w-3 text-emerald-400"/> Cash · <span className="text-emerald-300 font-bold normal-case tracking-normal">Collected at pickup</span></span>
                <span className="flex items-center gap-1.5"><CreditCard className="h-3 w-3 text-indigo-300"/> Card · <span className="text-indigo-200 font-bold normal-case tracking-normal">Pending Square payout</span></span>
                <span className="text-zinc-600">Last refreshed {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"}</span>
              </div>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
